import type { AdminRouterContext } from '@/lib/router-context'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import ReportDateRangeFilterBar, {
  type ReportDateRangeSelection,
} from '@/components/reports/ReportDateRangeFilterBar'
import LoadingSpinner from '@/components/ui/loading-spinner'
import { usePathHelper } from '@/hooks/usePathHelper'
import {
  REPORT_DATE_MODE,
  getCurrentReportDateFilters,
  getOrganizationReportDateBounds,
} from '@/lib/report-date-range'
import {
  organizationQueries,
  useOrganization,
} from '@/lib/services/organizations/organizations.query'
import {
  reportQueries,
  useExportPhoneClickReportMutation,
  usePhoneClickReport,
} from '@/lib/services/reports/reports.query'
import { prefetchIndexTableRouteQuery } from '@/lib/services/index-table-query-options'
import {
  PHONE_CLICK_REPORT_MAX_RANGE_DAYS,
  getPhoneClickReportFiltersFromSearch,
  parsePhoneClickGranularity,
  phoneClickReportSearchSchema,
  resolvePhoneClickGranularityForRange,
  type PhoneClickReportFilters,
  type PhoneClickReportSearch,
} from '@/lib/services/reports/reports.schema'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Download, PhoneCall } from 'lucide-react'
import { startTransition } from 'react'
import { toast } from 'sonner'
import { downloadExportedReport, getExportErrorMessage } from '../-helpers'
import { getOrganizationNow } from '@/lib/reporting-dates'
import { PhoneClickReportResults } from './-results'

/** A details-table change: a page, a page size, or a sort (which returns to page 1). */
type PhoneClickTableChange = {
  page?: number
  page_size?: number
  sort?: string
}

export async function loadPhoneClickReport({
  context,
  deps: { search },
  params,
}: {
  context: Pick<AdminRouterContext, 'queryClient'>
  deps: { search: PhoneClickReportSearch }
  params: { orgId: number }
}): Promise<void> {
  if (Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)) {
    return
  }

  const organization =
    context.queryClient.getQueryData?.(organizationQueries.show(params.orgId).queryKey) ??
    (await context.queryClient.ensureQueryData(organizationQueries.show(params.orgId)))
  const filters = getReportFilters(search, organization?.timezone)

  void prefetchIndexTableRouteQuery(
    context.queryClient,
    reportQueries.phoneClicks({
      organizationId: params.orgId,
      filters,
    })
  )
}

export const Route = createFileRoute('/_authenticated/$orgId/reports/phone-clicks/')({
  component: PhoneClickReportPage,
  validateSearch: (search) => phoneClickReportSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ search }),
  loader: loadPhoneClickReport,
})

export function PhoneClickReportPage() {
  const navigate = useNavigate()
  const path = usePathHelper()
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const { data: organization } = useOrganization(orgId)
  const filters = getReportFilters(search, organization?.timezone)
  const granularity = resolvePhoneClickGranularityForRange(search.granularity, filters)
  const { data: report, isFetching } = usePhoneClickReport(filters)
  const exportMutation = useExportPhoneClickReportMutation()
  const organizationNow = getOrganizationNow(organization?.timezone)
  const datePickerBounds = getOrganizationReportDateBounds(
    organization?.created_at,
    organizationNow,
    organization?.timezone
  )
  const hasActivity = (report?.summary?.total_clicks ?? 0) > 0

  const applyFilters = ({ start_date, end_date, date_mode }: ReportDateRangeSelection) => {
    const resolvedGranularity = resolvePhoneClickGranularityForRange(granularity, {
      start_date,
      end_date,
    })

    startTransition(() => {
      void navigate({
        ...path.phoneClicks.index().using({ orgId }).link,
        search: {
          start_date,
          end_date,
          date_mode: date_mode ?? undefined,
          granularity: resolvedGranularity,
          page: 1,
          page_size: filters.page_size,
          sort: filters.sort,
        },
        replace:
          Boolean(search.start_date && search.end_date) ||
          Boolean(search.date_mode) ||
          Boolean(search.granularity),
      })
    })
  }

  const handleGranularityChange = (nextGranularity: string) => {
    const resolvedGranularity = resolvePhoneClickGranularityForRange(
      parsePhoneClickGranularity(nextGranularity),
      filters
    )

    startTransition(() => {
      void navigate({
        ...path.phoneClicks.index().using({ orgId }).link,
        search: {
          start_date: filters.start_date,
          end_date: filters.end_date,
          date_mode: filters.date_mode ?? undefined,
          granularity: resolvedGranularity,
          page: filters.page,
          page_size: filters.page_size,
          sort: filters.sort,
        },
        replace:
          Boolean(search.start_date) ||
          Boolean(search.end_date) ||
          Boolean(search.date_mode) ||
          Boolean(search.granularity),
      })
    })
  }

  const handleTableChange = ({ page, page_size, sort }: PhoneClickTableChange) => {
    startTransition(() => {
      void navigate({
        ...path.phoneClicks.index().using({ orgId }).link,
        search: {
          start_date: filters.start_date,
          end_date: filters.end_date,
          date_mode: filters.date_mode ?? undefined,
          granularity,
          page: page ?? filters.page,
          page_size: page_size ?? filters.page_size,
          sort: sort ?? filters.sort,
        },
        replace: true,
      })
    })
  }

  const handleExport = async () => {
    try {
      const response = await exportMutation.mutateAsync({
        organizationId: orgId,
        filters: {
          start_date: filters.start_date,
          end_date: filters.end_date,
          date_mode: filters.date_mode,
          sort: filters.sort,
        },
      })

      downloadExportedReport(
        response,
        `mobile-call-report-${filters.start_date}-to-${filters.end_date}.xlsx`
      )
    } catch (error) {
      toast.error(
        await getExportErrorMessage(error, 'Unable to export the mobile call report right now.')
      )
    }
  }

  return (
    <div className="@container space-y-4 pb-6">
      <AdminPageHeader
        icon={PhoneCall}
        title="Mobile Call Report"
        description="See when shoppers click/tap a phone number on your website."
        className="mt-2"
        actions={
          <>
            <ReportDateRangeFilterBar
              appliedDateMode={REPORT_DATE_MODE.CUSTOM}
              initialFilters={{
                ...filters,
                ...datePickerBounds,
              }}
              maximumRangeDays={PHONE_CLICK_REPORT_MAX_RANGE_DAYS}
              now={organizationNow}
              onApply={applyFilters}
            />
            <AdminHeaderActionButton
              type="button"
              variant="outline"
              onClick={handleExport}
              disabled={!hasActivity || exportMutation.isPending}
              aria-busy={exportMutation.isPending || undefined}
            >
              {exportMutation.isPending ? (
                <LoadingSpinner className="size-4" />
              ) : (
                <Download className="size-4" />
              )}
              {exportMutation.isPending ? 'Exporting…' : 'Export Spreadsheet'}
            </AdminHeaderActionButton>
          </>
        }
      />

      <PhoneClickReportResults
        report={report}
        selectedRange={filters}
        granularity={granularity}
        onGranularityChange={handleGranularityChange}
        onPaginationChange={handleTableChange}
        sort={filters.sort}
        onSortChange={(sort) => handleTableChange({ sort, page: 1 })}
        isFetching={isFetching}
      />
    </div>
  )
}

function getReportFilters(
  search: PhoneClickReportSearch,
  organizationTimezone: string
): PhoneClickReportFilters {
  const defaultFilters = getCurrentReportDateFilters(getOrganizationNow(organizationTimezone))

  return getPhoneClickReportFiltersFromSearch(search, defaultFilters)
}
